// --- src/services/posService.ts ---
import { supabase } from '@/lib/supabaseClient';
import { SupplierProduct, TransactionResult } from '../components/types';
import { logger } from './logger';
import { syncService } from './syncService';
import { eventBus, EVENT_TYPES } from '../utils/eventBus';
import { cacheService } from './cacheService';
import { safeParseOfflineUser } from '../utils/safeParse';

// Cache key constant
const CACHE_KEY_INVENTORY = 'inventory_cache';

/**
 * Helper function to get the correct RPC name based on the active schema.
 * If the schema is 'leltarmester_dev', appends '_dev' suffix to the base name.
 * If the schema is 'leltarmester_prod', appends '_prod' suffix to the base name.
 * Default fallback: baseName + '_prod'
 *
 * @param baseName - Base RPC function name (e.g., 'process_checkout_transaction')
 * @returns RPC function name with appropriate suffix if needed
 */
const getRpcName = (baseName: string): string => {
  const schema = import.meta.env.VITE_SUPABASE_SCHEMA;
  // If schema is 'leltarmester_prod', return baseName + '_prod'
  // If schema is 'leltarmester_dev', return baseName + '_dev'
  // Default fallback: baseName + '_prod'
  if (schema === 'leltarmester_dev') return `${baseName}_dev`;
  if (schema === 'leltarmester_prod') return `${baseName}_prod`;
  return `${baseName}_prod`;
};

/**
 * TASK 2: Helper function to get user for transactions (online or offline)
 *
 * This function implements the offline authentication strategy:
 * 1. Try online authentication first (supabase.auth.getUser())
 * 2. If offline or auth fails, try cached user from localStorage
 * 3. Only throw error if BOTH fail
 *
 * @returns User object with id and email
 * @throws Error if user not authenticated (both online and offline fail)
 */
const getUserForTransaction = async (): Promise<{ id: string; email?: string }> => {
  // Try online authentication first
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (user && !authError) {
    logger.debug('Using online user authentication', { userId: user.id }, 'posService');
    return user;
  }

  // If offline or auth failed, try cached user
  if (!navigator.onLine || authError) {
    const cached = localStorage.getItem('offline_user');
    const userData = safeParseOfflineUser(cached);

    if (userData) {
      logger.info('Using cached user (offline mode)', { userId: userData.id }, 'posService');
      return { id: userData.id, email: userData.email };
    }
  }

  // Only throw if BOTH online and offline authentication fail
  logger.error(
    'User not authenticated (both online and offline failed)',
    { isOnline: navigator.onLine, authError },
    'posService'
  );
  throw new Error('User not authenticated');
};

export const posService = {
  // ===== POS CART TRANSACTIONS =====

  /**
   * Creates a POS cart transaction for history and receipt viewing
   * This should be called after successful checkout
   */
  async createCartTransaction(
    paymentType: 'CASH' | 'INVOICE' | 'CARD' | 'OTHER',
    cartItems: Array<{ ean: string; name: string; price: number; quantity: number }>,
    notes?: string
  ): Promise<string | null> {
    try {
      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      // Prepare cart items for RPC
      const itemsJson = cartItems.map((item) => ({
        ean: item.ean,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
      }));

      // Call RPC function (Phase 4: Using getRpcName for safety)
      const { data, error } = await supabase.rpc(getRpcName('process_pos_cart_transaction'), {
        p_user_id: user.id,
        p_payment_type: paymentType,
        p_cart_items: itemsJson,
        p_notes: notes || null,
      });

      if (error) {
        console.error('Error creating cart transaction:', error);
        // Don't throw - cart transaction logging is non-critical
        return null;
      }

      return data as string; // Returns cart_transaction_id
    } catch (error) {
      console.error('Error in createCartTransaction:', error);
      // Don't throw - cart transaction logging is non-critical
      return null;
    }
  },

  /**
   * Processes checkout transaction atomically (batch processing)
   *
   * 🔥 ATOMBIZTOSAN MŰKÖDŐ OFFLINE-FIRST IMPLEMENTÁCIÓ
   * 3-SZINTŰ VÉDELEM:
   * 1. Explicit offline flag (forceOffline parameter)
   * 2. Browser offline detection (navigator.onLine)
   * 3. Network error detection with timeout (try-catch + timeout)
   *
   * This replaces the client-side loop and ensures all-or-nothing processing.
   */
  async processCheckoutBatch(
    paymentType: 'CASH' | 'INVOICE' | 'CARD' | 'OTHER',
    cartItems: Array<{
      ean: string;
      name: string;
      price: number;
      selling_price?: number;
      quantity: number;
    }>,
    notes?: string,
    sellerName?: string,
    pultName?: string,
    transactionType?: 'SALE' | 'RETURN',
    forceOffline?: boolean,
    discountPercent?: number
  ): Promise<{
    cart_transaction_id: string;
    updated_items: Array<{ ean: string; new_quantity: number }>;
  }> {
    try {
      // Get authenticated user (works offline via cached user)
      const user = await getUserForTransaction();

      // Prepare cart items for RPC
      // NOTE: Database constraint requires quantity to be positive
      // For RETURN mode: send positive quantity, but negative selling_price
      // so total_amount will be negative, which backend uses to determine RETURN vs SALE
      // For SALE mode: send positive quantity and positive selling_price
      const itemsJson = cartItems.map((item) => {
        const baseSellingPrice = item.selling_price || item.price;
        return {
          ean: item.ean,
          name: item.name,
          quantity: Math.abs(item.quantity), // Always positive (DB constraint)
          price: item.price,
          selling_price:
            transactionType === 'RETURN' ? -Math.abs(baseSellingPrice) : baseSellingPrice,
        };
      });

      // Build notes with seller name if provided
      const finalNotes = sellerName
        ? `Eladó: ${sellerName}${notes ? ' | ' + notes : ''}`
        : notes || null;

      // Construct payload (used for both online and offline modes)
      const payload = {
        p_user_id: user.id,
        p_payment_type: paymentType,
        p_items: itemsJson,
        p_notes: finalNotes,
        p_seller_name: sellerName || null,
        p_pult_name: pultName || null,
        p_discount_percent: discountPercent || 0,
      };

      // ===================================================================
      // 🔥 LEVEL 1: Explicit offline flag (manual override or from network status)
      // ===================================================================
      if (forceOffline === true) {
        logger.warn(
          '🔴 FORCED OFFLINE MODE - Queueing transaction',
          { paymentType, itemCount: cartItems.length },
          'posService'
        );
        return await this.queueCheckoutTransaction(payload, itemsJson);
      }

      // ===================================================================
      // 🔥 LEVEL 2: Browser offline detection
      // ===================================================================
      if (!navigator.onLine) {
        logger.info(
          '📴 Browser offline detected - Queueing transaction',
          { paymentType, itemCount: cartItems.length },
          'posService'
        );
        return await this.queueCheckoutTransaction(payload, itemsJson);
      }

      // ===================================================================
      // 🔥 LEVEL 3: Try RPC call with timeout + network error detection
      // ===================================================================
      try {
        logger.debug('🌐 Attempting online checkout...', undefined, 'posService');

        // Create promise with timeout
        const timeoutDuration = 10000; // 10 seconds
        const rpcPromise = supabase.rpc(getRpcName('process_checkout_transaction'), payload);

        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('RPC_TIMEOUT'));
          }, timeoutDuration);
        });

        // Race between RPC call and timeout
        const { data, error } = await Promise.race([rpcPromise, timeoutPromise]);

        // Handle RPC errors
        if (error) {
          // Check if it's a network error
          const isNetworkError =
            error.message?.includes('fetch') ||
            error.message?.includes('Failed to fetch') ||
            error.message?.includes('NetworkError') ||
            error.message?.includes('ERR_') ||
            error.message?.includes('ENOTFOUND') ||
            error.message?.includes('ECONNREFUSED') ||
            error.code === 'PGRST301' || // PostgREST connection error
            error.code === 'PGRST000'; // PostgREST generic error

          if (isNetworkError) {
            logger.warn(
              '🌐❌ Network error during RPC call - Queueing transaction',
              { error: error.message, code: error.code },
              'posService'
            );

            // Emit offline event for UI/sync service
            eventBus.emit(EVENT_TYPES.SYNC_FAILED, {
              reason: 'Network error during checkout',
              error: error.message,
            });

            return await this.queueCheckoutTransaction(payload, itemsJson);
          }

          // If it's a business logic error (not network), throw it
          logger.error('❌ RPC business logic error', error, 'posService');
          throw new Error(error.message || 'Checkout transaction failed');
        }

        if (!data) {
          throw new Error('No data returned from checkout transaction');
        }

        // Success! Parse the JSONB response
        const result = data as {
          cart_transaction_id: string;
          updated_items: Array<{ ean: string; new_quantity: number }>;
        };

        logger.info(
          '✅ Online checkout successful',
          { id: result.cart_transaction_id, items: result.updated_items.length },
          'posService'
        );

        // ===================================================================
        // ✅ CRITICAL: INVALIDATE CACHE after successful checkout
        // ===================================================================
        // This ensures that fetchAllData will fetch fresh data on next call
        // Without this, loadCloudData() gets stale cached data (causes UI to show old values)
        cacheService.invalidate('inventory:');
        console.log('[processCheckoutBatch] Cleared inventory cache entries');

        try {
          localStorage.removeItem(CACHE_KEY_INVENTORY);
          console.log('[processCheckoutBatch] Cleared localStorage inventory cache');
        } catch (e) {
          console.warn('[processCheckoutBatch] Failed to clear localStorage cache:', e);
        }

        return result;
      } catch (rpcError: any) {
        // Catch timeout or unexpected network errors
        const isNetworkIssue =
          rpcError.message === 'RPC_TIMEOUT' ||
          rpcError.name === 'AbortError' ||
          rpcError.message?.includes('fetch') ||
          rpcError.message?.includes('network') ||
          rpcError.message?.includes('timeout') ||
          rpcError.message?.includes('ENOTFOUND') ||
          rpcError.message?.includes('ECONNREFUSED');

        if (isNetworkIssue) {
          logger.warn(
            '⏱️ RPC call failed (timeout/network) - Queueing transaction',
            { error: rpcError.message },
            'posService'
          );

          // Emit offline event
          eventBus.emit(EVENT_TYPES.SYNC_FAILED, {
            reason: 'RPC timeout or network issue',
            error: rpcError.message,
          });

          return await this.queueCheckoutTransaction(payload, itemsJson);
        }

        // If it's not a network issue, re-throw (business logic error)
        throw rpcError;
      }
    } catch (error: any) {
      logger.error('❌ Critical error in processCheckoutBatch', error, 'posService');
      throw error;
    }
  },

  /**
   * 🆕 Helper function: Queue checkout transaction for offline processing
   *
   * This is called when:
   * 1. forceOffline = true (explicit)
   * 2. navigator.onLine = false (browser offline)
   * 3. RPC call fails with network error (timeout, fetch error, etc.)
   *
   * @param payload RPC payload
   * @param itemsJson Cart items array
   * @returns Mock response with offline transaction ID
   */
  async queueCheckoutTransaction(
    payload: any,
    itemsJson: Array<{
      ean: string;
      name: string;
      quantity: number;
      price: number;
      selling_price: number;
    }>
  ): Promise<{
    cart_transaction_id: string;
    updated_items: Array<{ ean: string; new_quantity: number }>;
  }> {
    logger.info(
      '📦 Queueing checkout transaction (offline)',
      { itemCount: itemsJson.length },
      'posService'
    );

    // Add to offline queue (IndexedDB)
    await syncService.addToQueue('process_checkout_transaction', payload, 'CHECKOUT');

    // Generate mock transaction ID
    const mockTransactionId = `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    logger.info(
      '✅ Checkout queued successfully',
      { mockTransactionId, queuedItems: itemsJson.length },
      'posService'
    );

    // Emit event for UI notification
    eventBus.emit(EVENT_TYPES.SYNC_PROGRESS, {
      action: 'checkout_queued',
      transactionId: mockTransactionId,
      itemCount: itemsJson.length,
    });

    // Return mock response (will be replaced with real data when synced)
    return {
      cart_transaction_id: mockTransactionId,
      updated_items: itemsJson.map((item) => ({
        ean: item.ean,
        new_quantity: 0, // Will be updated when synced online
      })),
    };
  },

  /**
   * Gets cart transaction history for the current user
   */
  async getCartTransactionHistory(limit: number = 50): Promise<
    Array<{
      id: string;
      payment_type: string;
      total_amount: number;
      total_items: number;
      created_at: string;
      seller_name?: string;
      pult_name?: string;
      notes?: string;
      discount_percent?: number;
      discount_amount?: number;
      items: Array<{
        ean: string;
        product_name: string;
        quantity: number;
        unit_price: number; // Gross unit price
        total_price: number; // Gross total price
        vat_rate?: number; // VAT rate for this item
        unit?: string; // Valid unit (db/kg/m) for display
      }>;
    }>
  > {
    try {
      // TASK 2: Use offline-aware authentication (return empty array on auth failure)
      let user;
      try {
        user = await getUserForTransaction();
      } catch (error) {
        console.warn('No user session for cart transaction history');
        return [];
      }

      // Fetch cart transactions (company-wide, RLS handles access control)
      const { data: transactions, error: transactionsError } = await supabase
        .from('pos_cart_transactions')
        .select(
          'id, payment_type, total_amount, total_items, created_at, seller_name, pult_name, notes, discount_percent, discount_amount'
        )
        .order('created_at', { ascending: false })
        .limit(limit);

      if (transactionsError) {
        console.error('Error fetching cart transactions:', transactionsError);
        return [];
      }

      if (!transactions || transactions.length === 0) {
        return [];
      }

      const transactionIds = transactions.map((t) => t.id);

      // Fetch cart transaction items
      const { data: items, error: itemsError } = await supabase
        .from('pos_cart_transaction_items')
        .select('cart_transaction_id, ean, product_name, quantity, unit_price, total_price')
        .in('cart_transaction_id', transactionIds)
        .order('created_at', { ascending: true });

      if (itemsError) {
        console.error('Error fetching cart transaction items:', itemsError);
        return transactions.map((t) => ({ ...t, items: [] }));
      }

      // Fetch VAT rates and unit from inventory_items for all unique EANs
      const eans = items ? [...new Set(items.map((item) => item.ean))] : [];
      let vatRateMap = new Map<string, number>();
      let unitMap = new Map<string, string>();

      if (eans.length > 0) {
        const { data: inventoryItems } = await supabase
          .from('inventory_items')
          .select('ean, vat_rate, unit')
          .in('ean', eans);

        inventoryItems?.forEach(
          (item: { ean: string; vat_rate?: number; unit?: string | null }) => {
            if (item.vat_rate) {
              vatRateMap.set(item.ean, item.vat_rate);
            }
            if (item.unit) {
              unitMap.set(item.ean, item.unit);
            }
          }
        );
      }

      // Group items by transaction
      const itemsByTransaction = new Map<string, typeof items>();
      items?.forEach((item) => {
        const transactionId = item.cart_transaction_id;
        if (!itemsByTransaction.has(transactionId)) {
          itemsByTransaction.set(transactionId, []);
        }
        itemsByTransaction.get(transactionId)!.push(item);
      });

      // Combine transactions with their items (add VAT rate)
      return transactions.map((transaction) => {
        const transactionItems =
          itemsByTransaction.get(transaction.id)?.map((item) => ({
            ean: item.ean,
            product_name: item.product_name,
            quantity: item.quantity,
            unit_price: Number(item.unit_price),
            total_price: Number(item.total_price),
            vat_rate: vatRateMap.get(item.ean) || 27, // Default to 27% if not found
            unit: unitMap.get(item.ean) || 'db', // Valid unit (db/kg/m) for display
          })) || [];

        // Calculate totals from items if missing or 0
        const calculatedTotal = transactionItems.reduce((sum, item) => sum + item.total_price, 0);
        const calculatedItems = transactionItems.reduce((sum, item) => sum + item.quantity, 0);

        return {
          id: transaction.id,
          payment_type: transaction.payment_type,
          total_amount: Number(transaction.total_amount) || calculatedTotal || 0,
          total_items: transaction.total_items || calculatedItems || 0,
          created_at: transaction.created_at,
          seller_name: (transaction as any).seller_name || undefined,
          pult_name: (transaction as any).pult_name || undefined,
          notes: (transaction as any).notes || undefined,
          discount_percent: Number((transaction as any).discount_percent) || 0,
          discount_amount: Number((transaction as any).discount_amount) || 0,
          items: transactionItems,
        };
      });
    } catch (error) {
      console.error('Error in getCartTransactionHistory:', error);
      return [];
    }
  },

  /**
   * Visszavonás funkció - törli a kosár tranzakciót és visszaállítja a készletet
   * @param transactionId - A törlendő tranzakció ID-ja
   * @param updateItemQuantity - Function from inventoryService to update inventory
   */
  async cancelCartTransaction(
    transactionId: string,
    updateItemQuantity?: (
      ean: string,
      delta: number,
      productDetails?: SupplierProduct,
      reason?: 'RETURN',
      isSyncing?: boolean
    ) => Promise<TransactionResult | null>
  ): Promise<boolean> {
    try {
      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      // 1. Lekérjük a tranzakció tételeit
      const { data: items, error: itemsError } = await supabase
        .from('pos_cart_transaction_items')
        .select('ean, quantity, unit_price')
        .eq('cart_transaction_id', transactionId);

      if (itemsError) {
        throw new Error(`Failed to fetch transaction items: ${itemsError.message}`);
      }

      if (!items || items.length === 0) {
        // Ha nincsenek tételek az adatbázisban, csak töröljük a tranzakciót
        // (lehet, hogy már törölve lett, vagy soha nem voltak tételek)
        console.warn(`Transaction ${transactionId} has no items - deleting transaction only`);

        const { error: deleteError } = await supabase
          .from('pos_cart_transactions')
          .delete()
          .eq('id', transactionId)
          .eq('user_id', user.id);

        if (deleteError) {
          throw new Error(`Failed to delete transaction: ${deleteError.message}`);
        }

        return true; // Sikeres törlés, még ha nincsenek is tételek
      }

      // 2. Visszaállítjuk a készletet (RETURN mód - hozzáadás)
      if (updateItemQuantity) {
        for (const item of items) {
          // Query inventory_items instead of supplier_products (RLS handles company_id filtering)
          const { data: inventoryData } = await supabase
            .from('inventory_items')
            .select('name, sku, net_price, net_selling_price, selling_price, vat_rate')
            .eq('ean', item.ean)
            .maybeSingle();

          const productDetails = inventoryData
            ? {
                ean: item.ean,
                name: inventoryData.name || '',
                sku: inventoryData.sku || '',
                netPrice: Number(inventoryData.net_price) || 0,
                netSellingPrice: Number(inventoryData.net_selling_price) || 0,
                sellingPrice: Number(inventoryData.selling_price) || 0,
                vatRate: Number(inventoryData.vat_rate) || 27,
              }
            : undefined;

          // Visszaadjuk a készletet (RETURN mód - pozitív delta)
          await updateItemQuantity(
            item.ean,
            item.quantity, // Mennyiség, amit visszaadunk
            productDetails,
            'RETURN',
            false
          );
        }
      } else {
        console.warn('updateItemQuantity not provided - skipping inventory restoration');
      }

      // 3. Töröljük a tranzakciót (CASCADE miatt a tételek is törlődnek)
      const { error: deleteError } = await supabase
        .from('pos_cart_transactions')
        .delete()
        .eq('id', transactionId)
        .eq('user_id', user.id); // Biztonság: csak saját tranzakciót törölhet

      if (deleteError) {
        throw new Error(`Failed to delete transaction: ${deleteError.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error canceling cart transaction:', error);
      throw error;
    }
  },

  /**
   * Visszatérítés kezelése - egy terméket visszavásárol (RETURN mód)
   * @param ean - Termék EAN kódja
   * @param quantity - Visszavásárolt mennyiség
   * @param productDetails - Termék adatok
   * @param updateItemQuantity - Function from inventoryService to update inventory
   */
  async processReturn(
    ean: string,
    quantity: number,
    productDetails?: SupplierProduct,
    updateItemQuantity?: (
      ean: string,
      delta: number,
      productDetails?: SupplierProduct,
      reason?: 'RETURN',
      isSyncing?: boolean
    ) => Promise<TransactionResult | null>
  ): Promise<TransactionResult | null> {
    if (!updateItemQuantity) {
      throw new Error('updateItemQuantity function is required for processReturn');
    }
    return updateItemQuantity(ean, quantity, productDetails, 'RETURN', false);
  },

  /**
   * Napi zárás funkció - napi összesítés és zárás
   * @param date - Dátum (opcionális, alapból ma)
   */
  async getDailyClosure(date?: Date): Promise<{
    date: string;
    totalTransactions: number;
    totalRevenue: number;
    totalItems: number;
    transactionsByPaymentType: Record<string, { count: number; amount: number }>;
    transactions: Array<{
      id: string;
      payment_type: string;
      total_amount: number;
      total_items: number;
      created_at: string;
    }>;
  }> {
    try {
      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      const targetDate = date || new Date();
      const startOfDay = new Date(targetDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(targetDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Lekérjük az adott nap tranzakcióit (összes terminál - RLS handles company_id filtering)
      const { data: transactions, error: transactionsError } = await supabase
        .from('pos_cart_transactions')
        .select('id, payment_type, total_amount, total_items, created_at')
        .gte('created_at', startOfDay.toISOString())
        .lte('created_at', endOfDay.toISOString())
        .order('created_at', { ascending: false });

      if (transactionsError) {
        throw new Error(`Failed to fetch transactions: ${transactionsError.message}`);
      }

      // Összesítések számítása
      let totalRevenue = 0;
      let totalItems = 0;
      const transactionsByPaymentType: Record<string, { count: number; amount: number }> = {
        CASH: { count: 0, amount: 0 },
        CARD: { count: 0, amount: 0 },
        INVOICE: { count: 0, amount: 0 },
        OTHER: { count: 0, amount: 0 },
      };

      transactions?.forEach((transaction) => {
        const amount = Number(transaction.total_amount);
        const items = transaction.total_items;
        const paymentType = transaction.payment_type;

        totalRevenue += amount;
        totalItems += items;

        if (transactionsByPaymentType[paymentType]) {
          transactionsByPaymentType[paymentType].count++;
          transactionsByPaymentType[paymentType].amount += amount;
        }
      });

      return {
        date: targetDate.toISOString().split('T')[0],
        totalTransactions: transactions?.length || 0,
        totalRevenue,
        totalItems,
        transactionsByPaymentType,
        transactions: (transactions || []).map((t) => ({
          id: t.id,
          payment_type: t.payment_type,
          total_amount: Number(t.total_amount),
          total_items: t.total_items,
          created_at: t.created_at,
        })),
      };
    } catch (error) {
      console.error('Error getting daily closure:', error);
      throw error;
    }
  },

  /**
   * Parkolás funkció - kosár elmentése későbbi folytatáshoz
   * @param cartItems - Kosár tételek
   * @param notes - Opcionális megjegyzés
   */
  async suspendCart(
    cartItems: Array<{ ean: string; name: string; price: number; quantity: number }>,
    notes?: string
  ): Promise<string> {
    try {
      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      if (!cartItems || cartItems.length === 0) {
        throw new Error('Cart is empty');
      }

      const totalAmount = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

      const { data, error } = await supabase
        .from('pos_suspended_carts')
        .insert({
          user_id: user.id,
          cart_items: cartItems,
          total_amount: totalAmount,
          total_items: totalItems,
          notes: notes || null,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 óra
        })
        .select('id')
        .single();

      if (error) {
        logger.error('Error suspending cart:', error);
        throw new Error(`Failed to suspend cart: ${error.message}`);
      }

      return data.id;
    } catch (error) {
      console.error('Error in suspendCart:', error);
      throw error;
    }
  },

  /**
   * Parkolt kosarak listázása (csak aktívak - még nem folytatottak)
   */
  async getSuspendedCarts(): Promise<
    Array<{
      id: string;
      cart_items: Array<{ ean: string; name: string; price: number; quantity: number }>;
      total_amount: number;
      total_items: number;
      notes?: string;
      created_at: string;
      expires_at: string;
    }>
  > {
    try {
      // TASK 2: Use offline-aware authentication (return empty array on auth failure)
      let user;
      try {
        user = await getUserForTransaction();
      } catch (error) {
        console.warn('No user session for suspended carts');
        return [];
      }

      // Csak az aktív parkolt kosarakat (resumed_at IS NULL és még nem jártak le)
      const { data, error } = await supabase
        .from('pos_suspended_carts')
        .select('id, cart_items, total_amount, total_items, notes, created_at, expires_at')
        .eq('user_id', user.id)
        .is('resumed_at', null)
        .gt('expires_at', new Date().toISOString()) // Még nem jártak le
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching suspended carts:', error);
        return [];
      }

      // Töröljük a lejárt kosarakat (ha vannak)
      if (data && data.length > 0) {
        const now = new Date();
        const expiredIds = data
          .filter((cart) => new Date(cart.expires_at) < now)
          .map((cart) => cart.id);

        if (expiredIds.length > 0) {
          // Töröljük a lejárt kosarakat (csendben, nem dobunk hibát)
          await supabase.from('pos_suspended_carts').delete().in('id', expiredIds);
        }
      }

      return (
        data?.map((cart) => ({
          id: cart.id,
          cart_items: cart.cart_items as Array<{
            ean: string;
            name: string;
            price: number;
            quantity: number;
          }>,
          total_amount: Number(cart.total_amount),
          total_items: cart.total_items,
          notes: cart.notes || undefined,
          created_at: cart.created_at,
          expires_at: cart.expires_at,
        })) || []
      );
    } catch (error) {
      console.error('Error in getSuspendedCarts:', error);
      return [];
    }
  },

  /**
   * Parkolt kosár folytatása - betölti a kosár tartalmát
   * @param suspendedCartId - Parkolt kosár ID-ja
   */
  async resumeSuspendedCart(suspendedCartId: string): Promise<{
    cart_items: Array<{ ean: string; name: string; price: number; quantity: number }>;
    total_amount: number;
    total_items: number;
  }> {
    try {
      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      // Lekérjük a parkolt kosarat
      const { data, error } = await supabase
        .from('pos_suspended_carts')
        .select('cart_items, total_amount, total_items, expires_at, resumed_at')
        .eq('id', suspendedCartId)
        .eq('user_id', user.id)
        .is('resumed_at', null)
        .single();

      if (error) {
        throw new Error(`Failed to fetch suspended cart: ${error.message}`);
      }

      if (!data) {
        throw new Error('Suspended cart not found');
      }

      // Ellenőrizzük, hogy lejárt-e
      if (new Date(data.expires_at) < new Date()) {
        // Töröljük a lejárt kosarat
        await supabase.from('pos_suspended_carts').delete().eq('id', suspendedCartId);
        throw new Error('Suspended cart has expired');
      }

      // Jelöljük meg, hogy folytatva lett
      await supabase
        .from('pos_suspended_carts')
        .update({ resumed_at: new Date().toISOString() })
        .eq('id', suspendedCartId);

      return {
        cart_items: data.cart_items as Array<{
          ean: string;
          name: string;
          price: number;
          quantity: number;
        }>,
        total_amount: Number(data.total_amount),
        total_items: data.total_items,
      };
    } catch (error) {
      console.error('Error in resumeSuspendedCart:', error);
      throw error;
    }
  },

  /**
   * Parkolt kosár törlése
   * @param suspendedCartId - Parkolt kosár ID-ja
   */
  async deleteSuspendedCart(suspendedCartId: string): Promise<boolean> {
    try {
      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      // RLS handles company_id filtering - allows deleting any cart in the company
      const { error } = await supabase
        .from('pos_suspended_carts')
        .delete()
        .eq('id', suspendedCartId);

      if (error) {
        throw new Error(`Failed to delete suspended cart: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error in deleteSuspendedCart:', error);
      throw error;
    }
  },

  /**
   * Stock Reservation - Készlet foglalás létrehozása checkout kezdetekor
   * @param cartItems - Kosár tételek
   * @returns Reservation ID-k tömbje
   */
  async createStockReservations(
    cartItems: Array<{ ean: string; quantity: number }>
  ): Promise<string[]> {
    try {
      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      if (!cartItems || cartItems.length === 0) {
        throw new Error('Cart is empty');
      }

      // Először validáljuk, hogy van-e elég készlet (foglalásokkal együtt)
      const validationErrors: string[] = [];
      for (const item of cartItems) {
        // Phase 4: Using getRpcName for safety
        const { data: availableStock, error: stockError } = await supabase.rpc(
          getRpcName('get_available_stock'),
          { p_ean: item.ean }
        );

        if (stockError) {
          console.error('Error getting available stock:', stockError);
          validationErrors.push(`${item.ean}: Hiba a készlet lekérdezésekor`);
          continue;
        }

        if (availableStock < item.quantity) {
          validationErrors.push(
            `${item.ean}: Nincs elég készlet (elérhető: ${availableStock}, kért: ${item.quantity})`
          );
        }
      }

      if (validationErrors.length > 0) {
        throw new Error(`Nincs elég készlet:\n${validationErrors.join('\n')}`);
      }

      // Ha minden rendben, hozzuk létre a foglalásokat
      const reservations = cartItems.map((item) => ({
        user_id: user.id,
        ean: item.ean,
        quantity: item.quantity,
        status: 'PENDING',
        expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString(), // 10 perc
      }));

      const { data, error } = await supabase
        .from('stock_reservations')
        .insert(reservations)
        .select('id');

      if (error) {
        logger.error('Error creating stock reservations:', error);
        throw new Error(`Failed to create reservations: ${error.message}`);
      }

      return data.map((r) => r.id);
    } catch (error) {
      console.error('Error in createStockReservations:', error);
      throw error;
    }
  },

  /**
   * Stock Reservation - Foglalások törlése (sikeres vagy sikertelen checkout után)
   * @param reservationIds - Foglalás ID-k tömbje
   */
  async deleteStockReservations(reservationIds: string[]): Promise<boolean> {
    try {
      if (!reservationIds || reservationIds.length === 0) {
        return true; // Nincs mit törölni
      }

      // TASK 2: Use offline-aware authentication
      const user = await getUserForTransaction();

      const { error } = await supabase
        .from('stock_reservations')
        .delete()
        .in('id', reservationIds)
        .eq('user_id', user.id); // Biztonság: csak saját foglalásokat törölhet

      if (error) {
        logger.error('Error deleting stock reservations:', error);
        throw new Error(`Failed to delete reservations: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error in deleteStockReservations:', error);
      throw error;
    }
  },

  /**
   * Subscribes to real-time transaction updates
   * @param onNewTransaction - Callback when a new transaction is inserted
   * @returns Cleanup function to unsubscribe
   */
  subscribeToTransactions(onNewTransaction: () => void): () => void {
    const schemaName = import.meta.env.VITE_SUPABASE_SCHEMA || 'public';
    const channel = supabase
      .channel(`pos_transactions_${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: schemaName,
          table: 'pos_cart_transactions',
        },
        (payload) => {
          console.log('[subscribeToTransactions] New transaction detected:', payload);
          onNewTransaction();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};
